// Executioner's axe on its way back: while a follow-up attack is waiting, an axe hangs spinning over the lane it
// will come back to, with a red ring on the lane and a small label saying when. It speeds up and glows hotter on
// the turn it lands. (The landing itself is the 'axe-return' look, played by the table when the attack lands.)
import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

export interface AxeMark {
  key: string; // one mark per lane, side and attack
  x: number; // the lane, in table coordinates
  z: number;
  w: number; // the lane's size, for the ring
  d: number;
  label: string;
  due: boolean; // lands at the end of the turn being played now
}

interface Live {
  group: THREE.Group;
  axe: THREE.Sprite;
  ring: THREE.Mesh;
  label: HTMLElement;
  due: boolean;
  phase: number;
}

// the hanging axe's size (table units: a card is 6.35 wide) and how fast it turns (radians a second), and faster on
// the turn it lands
const AXE_SIZE = 4.6;
const SPIN = 0.7;
const SPIN_DUE = 2;

let axeTex: THREE.Texture | null = null;
/** A small throwing axe: a dark wooden haft and a steel crescent blade with a red edge. */
function axeTexture() {
  if (axeTex) return axeTex;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  g.translate(64, 64);
  g.rotate(-0.6);
  // haft
  g.fillStyle = '#5a3a22';
  g.strokeStyle = '#1b120b';
  g.lineWidth = 3;
  g.beginPath();
  g.roundRect(-5, -46, 10, 96, 4);
  g.fill();
  g.stroke();
  g.fillStyle = '#3a2416';
  for (let i = 0; i < 3; i++) g.fillRect(-5, 22 + i * 7, 10, 3); // grip wraps
  // blade
  const blade = new Path2D();
  blade.moveTo(2, -44);
  blade.quadraticCurveTo(48, -40, 50, -4);
  blade.quadraticCurveTo(30, -16, 2, -14);
  blade.closePath();
  const steel = g.createLinearGradient(2, -44, 50, -4);
  steel.addColorStop(0, '#9aa3ad');
  steel.addColorStop(0.6, '#e4e9ee');
  steel.addColorStop(1, '#b8c0c8');
  g.fillStyle = steel;
  g.fill(blade);
  g.lineWidth = 3;
  g.strokeStyle = '#20252b';
  g.stroke(blade);
  // the cutting edge, glowing red
  g.shadowColor = '#ff2a2a';
  g.shadowBlur = 10;
  g.strokeStyle = '#ff5a4a';
  g.lineWidth = 3;
  g.beginPath();
  g.moveTo(26, -38);
  g.quadraticCurveTo(48, -30, 49, -7);
  g.stroke();
  axeTex = new THREE.CanvasTexture(c);
  axeTex.colorSpace = THREE.SRGBColorSpace;
  return axeTex;
}

export class AxeMarks {
  private live = new Map<string, Live>();
  private scene: THREE.Scene;
  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  sync(marks: AxeMark[]) {
    const want = new Set(marks.map((m) => m.key));
    for (const [key, l] of this.live) {
      if (want.has(key)) continue;
      this.scene.remove(l.group);
      for (const m of [l.ring, ...(l.ring.children as THREE.Mesh[])]) {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
      (l.axe.material as THREE.Material).dispose();
      l.label.remove();
      this.live.delete(key);
    }
    for (const m of marks) {
      let l = this.live.get(m.key);
      if (!l) {
        const group = new THREE.Group();
        const r = Math.max(m.w, m.d) * 0.62; // round the whole lane, card and all
        const ringGeo = new THREE.RingGeometry(r * 0.86, r, 48);
        ringGeo.rotateX(-Math.PI / 2);
        const ring = new THREE.Mesh(ringGeo, new THREE.MeshBasicMaterial({ color: '#ff3b2f', transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending }));
        ring.position.y = 0.05;
        ring.renderOrder = 6;
        // a faint red wash over the lane under the ring
        const washGeo = new THREE.CircleGeometry(r * 0.9, 48);
        washGeo.rotateX(-Math.PI / 2);
        const wash = new THREE.Mesh(washGeo, new THREE.MeshBasicMaterial({ color: '#ff2a1a', transparent: true, opacity: 0.12, depthWrite: false, blending: THREE.AdditiveBlending }));
        wash.position.y = 0.04;
        ring.add(wash);
        const axe = new THREE.Sprite(new THREE.SpriteMaterial({ map: axeTexture(), transparent: true, depthWrite: false }));
        axe.scale.set(AXE_SIZE, AXE_SIZE, 1);
        axe.renderOrder = 7;
        const label = document.createElement('div');
        label.className = 'table-label axe-label';
        const lbl = new CSS2DObject(label);
        lbl.position.set(0, 0.5, m.d / 2 + 1.5);
        group.add(ring, axe, lbl);
        this.scene.add(group);
        l = { group, axe, ring, label, due: m.due, phase: Math.random() * 6.28 };
        this.live.set(m.key, l);
      }
      l.group.position.set(m.x, 0, m.z);
      l.label.textContent = m.label;
      l.label.classList.toggle('is-due', m.due);
      l.due = m.due;
    }
  }

  update(time: number) {
    for (const l of this.live.values()) {
      const t = time + l.phase;
      const speed = l.due ? SPIN_DUE : SPIN;
      l.axe.position.y = 5 + Math.sin(t * 1.3) * 0.5;
      (l.axe.material as THREE.SpriteMaterial).rotation = -t * speed; // spinning end over end, on its way back
      const pulse = 0.5 + 0.5 * Math.sin(t * (l.due ? 6 : 2.2));
      (l.ring.material as THREE.MeshBasicMaterial).opacity = (l.due ? 0.55 : 0.28) + pulse * (l.due ? 0.4 : 0.22);
      const s = 1 + pulse * (l.due ? 0.08 : 0.04);
      l.ring.scale.set(s, 1, s);
    }
  }

  dispose() {
    this.sync([]);
  }
}
